
import { useState } from 'react';
import { updateEvent, createEvent, EventStatus } from '@/services/eventService';
import { useToast } from "@/hooks/use-toast";
import { EventFormValues } from '../schemas/eventFormSchema';

interface UseEventSubmitProps {
  eventId: string | null;
  onEventUpdated: () => void;
  onClose: () => void;
}

export const useEventSubmit = ({ eventId, onEventUpdated, onClose }: UseEventSubmitProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  const handleSubmit = async (values: EventFormValues) => {
    setIsSubmitting(true);

    try {
      const toUtcISOString = (localDate: Date) =>
        new Date(localDate.getTime() - localDate.getTimezoneOffset() * 60000).toISOString();

      const getDateTimeRange = (
        dateStart: Date,
        dateEnd: Date,
        timeStart: Date,
        timeEnd: Date,
        timeNotSpecified: boolean
      ) => {
        if (timeNotSpecified) {
          const start = new Date(dateStart);
          start.setHours(0, 0, 0, 0);

          const end = new Date(dateEnd);
          end.setHours(23, 59, 59, 999);

          return {
            dateStart: toUtcISOString(start),
            dateEnd: toUtcISOString(end),
          };
        }

        const start = mergeDateAndTime(dateStart, timeStart);
        const end = mergeDateAndTime(dateEnd, timeEnd);

        return {
          dateStart: toUtcISOString(start),
          dateEnd: toUtcISOString(end),
        };
      };

      const mergeDateAndTime = (date: string | Date, time: string | Date): Date => {
        const d = new Date(date);
        const t = new Date(time);
        const merged = new Date(d);
        merged.setHours(t.getHours(), t.getMinutes(), t.getSeconds(), 0);
        return merged;
      };

      const { dateStart, dateEnd } = getDateTimeRange(values.startDate, values.endDate, values.startTime, values.endTime, values.timeNotSpecified);


      // Ensure credit points is undefined for post-accredited events
      const creditPoints = values.postAccredited
        ? null
        : (values.creditPoints ? Number(values.creditPoints) : null);

      const eventData = {
        name: values.eventName,
        description: values.description || "",
        dateStart,
        dateEnd,
        timeNotSpecified: values.timeNotSpecified,
        venue: values.isOnline
          ? {
            online: true,
            url: values.eventUrl,
            city: "",
            name: "Online",
            address: "",
            region: ""
          }
          : {
            city: values.city || "",
            name: values.venueName || "",
            address: values.address || "",
            region: values.region || "",
            online: false
          },
        type: values.eventType,
        professions: values.professions || [],
        creditPoints: creditPoints,
        postAccredited: values.postAccredited,
        maxAttendees: values.maxAttendees ? Number(values.maxAttendees) : undefined,
        imageUrl: values.imageUrl,
        registrationUrl: values.registrationUrl || undefined,
        status: values.status, // Ensure we use the status from form values
      };

      console.log("Submitting event with data:", JSON.stringify(eventData, null, 2));
      console.log("Event professions:", eventData.professions);
      console.log("Event status:", eventData.status);

      if (eventId) {
        await updateEvent(eventId, eventData);
        toast({
          title: "Esemény frissítve!",
          description: "Az esemény adatai sikeresen frissítve.",
        });
      } else {
        await createEvent(eventData);
        toast({
          title: "Esemény létrehozva!",
          description: "Az esemény sikeresen létrehozva.",
        });
      }

      onEventUpdated();
      onClose();
    } catch (error) {
      console.error('Error saving event:', error);
      toast({
        title: "Hiba történt!",
        description: "Nem sikerült menteni az eseményt.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    isSubmitting,
    handleSubmit,
  };
};
